import {
  addDays,
  BlockStatus,
  isBlockVisible,
  liveOnly,
  MarkOutcome,
  suggest,
  SuggestionKind,
  type LiveTimeBlock,
  type Suggestion,
} from '@wiztime/core';
import { queries } from '@wiztime/storage';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLive } from '../../../hooks/useLive';
import { useRunCommand } from '../../../hooks/useRunCommand';
import { useStore } from '../../../hooks/useServices';
import { formatRange } from '../../../lib/format';
import { Button } from '../../../ui/Button';
import { Card } from '../../../ui/Card';
import { useDayData } from '../DayDataContext';
import { useDayUi } from '../dayUiStore';
import styles from './Cards.module.css';

/** The "revisit" rule looks this far back for a project's last appearance. */
const LOOKBACK_DAYS = 14;

/** One-click suggestions for the current slot; the whole card is hidden when there are none. */
export function SuggestionCard() {
  const { t } = useTranslation();
  const store = useStore();
  const run = useRunCommand();
  const data = useDayData();
  const { date, settings, projects, now } = data;
  const recent = useLive(
    () => queries.blocksInRange(store.repo, addDays(date, -LOOKBACK_DAYS), date),
    [store, date],
  );

  const suggestions = useMemo(() => {
    if (recent.data === undefined || data.blocksLoading) return [];
    const blocksByDate: Record<string, LiveTimeBlock[]> = {};
    for (const block of liveOnly(recent.data)) {
      if (!isBlockVisible(block, settings, now)) continue;
      (blocksByDate[block.data.personalDate] ??= []).push(block);
    }
    return suggest({
      nowMs: now,
      date,
      settings,
      blocksByDate,
      projects: projects.all,
      draftsPending: data.blocks.filter((block) => block.data.status === BlockStatus.Draft).length,
    });
  }, [recent.data, data.blocksLoading, data.blocks, settings, now, date, projects.all]);

  if (suggestions.length === 0) return null;

  const name = (projectId: string): string =>
    projects.byId.get(projectId)?.data.name ?? t('timeline.unknownProject');
  const fill = (suggestion: Extract<Suggestion, { span: unknown }>): void => {
    // In quick-mark mode a successful suggestion counts as one mark (2026-10b §4.3): the
    // settle consumes the "once"; outside the mode it does nothing. The state is captured at
    // command start, so a late outcome never settles a state entered since.
    const startedFrom = useDayUi.getState().quickMark;
    void run(() => store.createBlock(date, suggestion.span, suggestion.projectId)).then((result) => {
      const outcome = !result.ok
        ? MarkOutcome.Failed
        : result.value === null
          ? MarkOutcome.Duplicate
          : MarkOutcome.Created;
      useDayUi.getState().quickMarkSettle(outcome, startedFrom);
    });
  };
  const reviewDrafts = (): void => {
    const draft = data.groups.find((group) => group.status === BlockStatus.Draft);
    if (draft === undefined) return;
    useDayUi.getState().select(draft.key);
    document
      .querySelector(`[data-block-id="${CSS.escape(draft.key)}"]`)
      ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };

  const rows = suggestions.map((suggestion) => {
    switch (suggestion.kind) {
      case SuggestionKind.FillSlot: {
        const range = formatRange(suggestion.span.startMin, suggestion.span.endMin);
        return {
          key: suggestion.kind,
          title: t('suggest.fillTitle', { range, project: name(suggestion.projectId) }),
          hint: t('suggest.fillHint'),
          action: t('suggest.fillAction'),
          run: () => fill(suggestion),
        };
      }
      case SuggestionKind.Continue:
        return {
          key: suggestion.kind,
          title: t('suggest.continueTitle', { project: name(suggestion.projectId) }),
          hint: t('suggest.continueHint', {
            range: formatRange(suggestion.span.startMin, suggestion.span.endMin),
          }),
          action: t('suggest.continueAction'),
          run: () => fill(suggestion),
        };
      case SuggestionKind.Revisit:
        return {
          key: suggestion.kind,
          title: t('suggest.revisitTitle', { project: name(suggestion.projectId) }),
          hint: t('suggest.revisitHint', {
            range: formatRange(suggestion.span.startMin, suggestion.span.endMin),
          }),
          action: t('suggest.revisitAction'),
          run: () => fill(suggestion),
        };
      case SuggestionKind.ConfirmDrafts:
        return {
          key: suggestion.kind,
          title: t('suggest.confirmTitle', { count: suggestion.count }),
          hint: t('suggest.confirmHint'),
          action: t('suggest.confirmAction'),
          run: reviewDrafts,
        };
    }
  });

  return (
    <Card title={t('suggest.title')} hint={t('suggest.hint')} testId="suggestion-card">
      <ul className={styles.suggestions}>
        {rows.map((row) => (
          <li key={row.key} className={styles.suggestion} data-testid={`suggestion-${row.key}`}>
            <div className={styles.suggestionText}>
              <p className={styles.suggestionTitle}>{row.title}</p>
              <p className={styles.mutedSmall}>{row.hint}</p>
            </div>
            <Button className={styles.suggestionAction} onClick={row.run}>
              {row.action}
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}
